import { Form, Select } from "antd";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useLoc } from "../lib/hooks";

import {
  ClickIdentifierFilter,
  ClickIdentifierSelect,
} from "./ClickIdentifierSelect";
import { DropdownPopover } from "./DropdownPopover";

type ClickIdentifiersSelectorValue = {
  gclid: string[];
  fbclid: string[];
  msclkid: string[];
  ttclid: string[];
};

type Props = {
  defaultValue?: ClickIdentifiersSelectorValue;
  filter?: Omit<Partial<ClickIdentifierFilter>, "type">;
  onChange: (changes: ClickIdentifiersSelectorValue) => void;
  style?: React.CSSProperties;
};

export const ClickIdentifiersSelector: React.FC<Props> = ({
  defaultValue,
  filter,
  style,
  onChange,
}) => {
  const loc = useLoc();
  const isTouched = useRef(false);
  const [gclid, setGclid] = useState<string[]>(defaultValue?.gclid ?? []);
  const [fbclid, setFbclid] = useState<string[]>(defaultValue?.fbclid ?? []);
  const [msclkid, setMsclkid] = useState<string[]>(defaultValue?.msclkid ?? []);
  const [ttclid, setTtclid] = useState<string[]>(defaultValue?.ttclid ?? []);

  useEffect(() => {
    if (!isTouched.current) return;
    onChange({ gclid, fbclid, msclkid, ttclid });
  }, [gclid, fbclid, msclkid, ttclid]);

  const value = useMemo(() => {
    if (
      gclid.length === 0 &&
      fbclid.length === 0 &&
      msclkid.length === 0 &&
      ttclid.length === 0
    )
      return null;

    return loc.word("ClickIdentifiersSelector.valueLabel", {
      default: "Click IDs are selected",
    });
  }, [gclid, fbclid, msclkid, ttclid, loc]);

  const clearIds = useCallback(() => {
    setGclid([]);
    setFbclid([]);
    setMsclkid([]);
    setTtclid([]);
  }, []);

  return (
    <DropdownPopover
      transparent
      shadow
      onOpenChange={(visible) => {
        if (visible) isTouched.current = true;
      }}
      trigger={["click"]}
      content={
        <Form
          onClick={(e) => e.stopPropagation()}
          layout="vertical"
          className="bg-white w-100 p-10"
        >
          <Form.Item className="flex-1" label="gclid">
            <ClickIdentifierSelect
              value={gclid}
              onChange={(x) => setGclid(x)}
              filter={{ type: "gclid", ...filter }}
            />
          </Form.Item>

          <Form.Item className="flex-1" label="fbclid">
            <ClickIdentifierSelect
              value={fbclid}
              onChange={(x) => setFbclid(x)}
              filter={{ type: "fbclid", ...filter }}
            />
          </Form.Item>

          <Form.Item className="flex-1" label="msclkid">
            <ClickIdentifierSelect
              value={msclkid}
              onChange={(x) => setMsclkid(x)}
              filter={{ type: "msclkid", ...filter }}
            />
          </Form.Item>

          <Form.Item className="flex-1" label="ttclid">
            <ClickIdentifierSelect
              value={ttclid}
              onChange={(x) => setTtclid(x)}
              filter={{ type: "ttclid", ...filter }}
            />
          </Form.Item>
        </Form>
      }
    >
      <Select
        value={value}
        allowClear
        onClear={clearIds}
        placeholder={loc.word("ClickIdentifiersSelector.placeholder", {
          default: "Select click IDs",
        })}
        open={false}
        style={style}
      />
    </DropdownPopover>
  );
};
